import { useState, useEffect } from "react";
import { Plus, Calendar, FileText, Check, X, Clock, DollarSign, Users } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { Skeleton } from "../components/ui/skeleton";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { DatePicker } from "../components/ui/DatePicker";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

import { payrollAPI, companyAPI } from "../services/api";
import { useCurrencyFormatter } from "../hooks/usePayrollFormatters";
import { formatDateFr } from "../components/payroll/payrollExportsHelpers";

const PayrollPeriods = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fmtCurrency = useCurrencyFormatter();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    description: "",
    period_type: "monthly",
  });

  const [companyQ, periodsQ] = useQueries({
    queries: [
      {
        queryKey: ["company-info"],
        queryFn: () => companyAPI.getCompanyInfo().then(d => d?.company ?? d).catch(() => ({ id: 1 })),
      },
      {
        queryKey: ["payroll-periods"],
        queryFn: () =>
          payrollAPI.getPayrollPeriods({ page: 1, limit: 20 }).then((data) => data.items || []),
      },
    ],
  });

  useEffect(() => {
    if (periodsQ.isError) toast.error(t("payrollPeriods.toasts.loadError"));
  }, [periodsQ.isError]);

  const companyId = companyQ.data?.id ?? 1;
  const periods = periodsQ.data ?? [];
  const loading = periodsQ.isPending;

  const handleCreatePeriod = async () => {
    try {
      if (!formData.start_date || !formData.end_date) {
        toast.error(t("payrollPeriods.toasts.datesRequired"));
        return;
      }

      const periodData = {
        ...formData,
        company_id: companyId ?? 1,
      };

      await payrollAPI.createPayrollPeriod(periodData);
      toast.success(t("payrollPeriods.toasts.createSuccess"));
      setCreateDialogOpen(false);
      setFormData({ start_date: "", end_date: "", description: "", period_type: "monthly" });
      queryClient.invalidateQueries({ queryKey: ["payroll-periods"] });
    } catch (error) {
      const detail = error?.backendDetail || error?.response?.data?.detail || error?.message;
      toast.error(detail || t("payrollPeriods.toasts.createError"));
    }
  };

  const handleCalculatePeriod = async (periodId) => {
    try {
      toast.info(t("payrollPeriods.toasts.calculating"));
      const result = await payrollAPI.calculatePayrollPeriod(periodId, {
        recalculate: false,
        period_id: periodId,
      });
      toast.success(t("payrollPeriods.toasts.calculateSuccess", { count: result.total_processed }));
      queryClient.invalidateQueries({ queryKey: ["payroll-periods"] });
    } catch (error) {
      const detail = error?.backendDetail || error?.response?.data?.detail || error?.message;
      toast.error(detail || t("payrollPeriods.toasts.calculateError"));
    }
  };

  const handleSubmitForApproval = async (periodId) => {
    try {
      await payrollAPI.submitPayrollPeriod(periodId, t("payrollPeriods.toasts.submitSuccess"));
      toast.success(t("payrollPeriods.toasts.submitSuccess"));
      queryClient.invalidateQueries({ queryKey: ["payroll-periods"] });
    } catch (error) {
      const detail = error?.backendDetail || error?.response?.data?.detail || error?.message;
      toast.error(detail || t("payrollPeriods.toasts.submitError"));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft:       { labelKey: "payrollPeriods.status.DRAFT",       variant: "secondary",    icon: Clock },
      calculating: { labelKey: "payrollPeriods.status.DRAFT",       variant: "secondary",    icon: Clock },
      calculated:  { labelKey: "payrollPeriods.status.CALCULATED",  variant: "default",      icon: FileText },
      submitted:   { labelKey: "payrollPeriods.status.SUBMITTED",   variant: "default",      icon: FileText },
      approved:    { labelKey: "payrollPeriods.status.APPROVED",    variant: "success",      icon: Check },
      rejected:    { labelKey: "payrollPeriods.status.REJECTED",    variant: "destructive",  icon: X },
      closed:      { labelKey: "payrollPeriods.status.CLOSED",      variant: "outline",      icon: DollarSign },
      cancelled:   { labelKey: "payrollPeriods.status.CLOSED",      variant: "outline",      icon: X },
    };

    const config = statusConfig[status?.toLowerCase()] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {t(config.labelKey)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-4 w-60" />
          </div>
          <Skeleton className="h-9 w-32" />
        </div>
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4 rounded-lg border p-4">
              <Skeleton className="h-10 w-10 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-32" />
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-8 w-8 rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">{t("payrollPeriods.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("payrollPeriods.subtitle")}</p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              {t("payrollPeriods.newPeriod")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{t("payrollPeriods.createDialogTitle")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("payrollPeriods.startDate")}</Label>
                  <DatePicker
                    value={formData.start_date}
                    onChange={(v) => setFormData({ ...formData, start_date: v })}
                  />
                </div>
                <div>
                  <Label>{t("payrollPeriods.endDate")}</Label>
                  <DatePicker
                    value={formData.end_date}
                    minDate={formData.start_date}
                    onChange={(v) => setFormData({ ...formData, end_date: v })}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="period_type">{t("payrollPeriods.periodType")}</Label>
                <Select
                  value={formData.period_type}
                  onValueChange={(value) => setFormData({ ...formData, period_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("payrollPeriods.periodTypePlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">{t("payrollPeriods.monthly")}</SelectItem>
                    <SelectItem value="weekly">{t("payrollPeriods.weekly")}</SelectItem>
                    <SelectItem value="biweekly">{t("payrollPeriods.biweekly")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="description">{t("payrollPeriods.description")}</Label>
                <Textarea
                  id="description"
                  placeholder={t("payrollPeriods.descriptionPlaceholder")}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>

              <Button onClick={handleCreatePeriod} className="w-full">
                {t("payrollPeriods.createButton")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Periods List */}
      {periods.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t("payrollPeriods.noPeriodsTitle")}</h3>
            <p className="text-muted-foreground">{t("payrollPeriods.noPeriodsDesc")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {periods.map((period) => (
            <Card key={period.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between pb-4">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {period.description || `${t("payrollPeriods.payOn")} ${formatDateFr(period.start_date)}`}
                    {getStatusBadge(period.status)}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("payrollPeriods.from")} {formatDateFr(period.start_date)}
                    {" "}{t("payrollPeriods.to")} {formatDateFr(period.end_date)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {period.status === "draft" && (
                    <Button variant="outline" size="sm" onClick={() => handleCalculatePeriod(period.id)}>
                      {t("payrollPeriods.calculate")}
                    </Button>
                  )}
                  {(period.status === "draft" || period.status === "calculated") && (
                    <Button variant="default" size="sm" onClick={() => handleSubmitForApproval(period.id)}>
                      {t("payrollPeriods.submit")}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{period.total_employees || 0}</p>
                      <p className="text-xs text-muted-foreground">{t("payrollPeriods.employees")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {fmtCurrency(period.total_gross_amount || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("payrollPeriods.totalGross")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {fmtCurrency(period.total_net_amount || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("payrollPeriods.totalNet")}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {fmtCurrency(period.total_deductions || 0)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("payrollPeriods.deductions")}</p>
                    </div>
                  </div>
                </div>

                {period.created_at && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-xs text-muted-foreground">
                      {t("payrollPeriods.createdOn")} {formatDateFr(period.created_at)}
                      {period.processed_at && ` • ${t("payrollPeriods.calculatedOn")} ${formatDateFr(period.processed_at)}`}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PayrollPeriods;
